// src/components/SyntraMfgMetaverseMap.jsx
import React, { useMemo, useState, useEffect, useRef } from 'react';

/**
 * Props:
 *  - rows (number) default 6
 *  - cols (number) default 8
 *  - dataset (array) default []  // listing objects: { id, name, price, img, owner, coords: {r,c}, type, location, area, marketPrice, offerPrice, unit }
 *  - ownerId (string) optional - highlights player's owned plots
 *  - onContact(listing) optional callback
 *  - onMakeOffer(listing, amount) optional callback
 */
export default function SyntraMfgMetaverseMap({
  rows = 6,
  cols = 8,
  dataset = [],
  ownerId = null,
  onContact = (it) => alert(`Contact requested for ${it.name || it.id} (dummy)`),
  onMakeOffer = (it, amount) => alert(`Offer ${amount} placed for ${it.name || it.id} (dummy)`),
}) {
  const [zoom, setZoom] = useState(1);
  const [showOwners, setShowOwners] = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCell, setSelectedCell] = useState(null); // {r,c,items}
  const [mapGrid, setMapGrid] = useState([]);
  const [colorScaleDomain, setColorScaleDomain] = useState([0, 1000]); // [min,max]

  // references
  const containerRef = useRef(null);
  const touchState = useRef({ initialDistance: null, initialZoom: null });

  // safe copy of dataset
  const listings = dataset || [];

  // compute min/max price for heatmap
  useEffect(() => {
    const prices = listings
      .map((l) => Number(l.price || l.offerPrice || l.marketPrice || 0))
      .filter((p) => !isNaN(p) && p > 0);
    if (prices.length > 0) {
      setColorScaleDomain([Math.min(...prices), Math.max(...prices)]);
    }
  }, [dataset]);

  // build grid and place items (preserve coords if provided)
  useEffect(() => {
    const total = rows * cols;
    const grid = Array.from({ length: rows }).map(() =>
      Array.from({ length: cols }).map(() => ({ items: [] }))
    );

    const clamp = (v, max) => Math.max(0, Math.min(max - 1, v));
    let idx = 0;
    for (const item of listings) {
      if (item && item.coords && typeof item.coords.r === 'number' && typeof item.coords.c === 'number') {
        const r = clamp(item.coords.r, rows);
        const c = clamp(item.coords.c, cols);
        grid[r][c].items.push(item);
      } else {
        const pos = idx % total;
        const r = Math.floor(pos / cols);
        const c = pos % cols;
        grid[r][c].items.push(item);
        idx++;
      }
    }
    setMapGrid(grid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset, rows, cols]);

  // color interpolation (green -> yellow -> red)
  const priceToColor = (price) => {
    const [min, max] = colorScaleDomain;
    if (!price || isNaN(price)) return '#ffffff';
    const t = max === min ? 0.5 : Math.max(0, Math.min(1, (price - min) / (max - min)));
    const lerp = (a, b, v) => Math.round(a + (b - a) * v);
    const green = [37, 160, 50];
    const yellow = [255, 200, 60];
    const red = [225, 50, 50];
    if (t < 0.5) {
      const v = t / 0.5;
      const rgb = [lerp(green[0], yellow[0], v), lerp(green[1], yellow[1], v), lerp(green[2], yellow[2], v)];
      return `rgb(${rgb.join(',')})`;
    } else {
      const v = (t - 0.5) / 0.5;
      const rgb = [lerp(yellow[0], red[0], v), lerp(yellow[1], red[1], v), lerp(yellow[2], red[2], v)];
      return `rgb(${rgb.join(',')})`;
    }
  };

  // derived totals
  const totalItems = useMemo(() => listings.length, [listings]);

  // search filter helper
  const cellMatchesSearch = (cell) => {
    if (!search) return true;
    const q = search.trim().toLowerCase();
    return cell.items.some(it => (
      (it.name && it.name.toLowerCase().includes(q)) ||
      (it.location && it.location.toLowerCase().includes(q)) ||
      (it.area && it.area.toLowerCase().includes(q))
    ));
  };

  // UI actions
  function openCell(r, c) {
    const items = (mapGrid[r] && mapGrid[r][c] && mapGrid[r][c].items) ? mapGrid[r][c].items : [];
    setSelectedCell({ r, c, items });
  }
  function closeDetails() { setSelectedCell(null); }

  // small action handlers
  function makeOffer(listing) {
    const amount = window.prompt(`Enter your offer amount for ${listing.name || listing.id}:`, listing.price || listing.offerPrice || '');
    if (!amount) return;
    onMakeOffer(listing, amount);
  }
  function contactOwner(listing) {
    onContact(listing);
  }
  function scheduleVisit(listing) {
    const dt = window.prompt('Preferred visit date/time (dummy):', '');
    if (!dt) { alert('Visit cancelled'); return; }
    alert(`Visit scheduled (dummy) for ${listing.name || listing.id} on ${dt}`);
  }

  // responsive cell sizing to avoid horizontal scrolling
  const [containerWidth, setContainerWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1200));
  const containerSizeRef = useRef(containerWidth);

  useEffect(() => {
    const updateWidth = () => {
      const w = containerRef.current ? containerRef.current.clientWidth : window.innerWidth;
      setContainerWidth(w);
      containerSizeRef.current = w;
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    // also observe container size if available
    let ro;
    if (containerRef.current && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => updateWidth());
      ro.observe(containerRef.current);
    }
    return () => {
      window.removeEventListener('resize', updateWidth);
      if (ro && containerRef.current) ro.unobserve(containerRef.current);
    };
  }, []);

  // base cell width in px (design default)
  const BASE_CELL = 120;
  // compute cellMin so that cols * cellMin <= containerWidth, avoiding horizontal overflow
  const cellMin = useMemo(() => {
    const maxAllowed = Math.floor((containerWidth - 12) / cols); // small padding margin
    const desired = Math.round(BASE_CELL * zoom);
    const clamped = Math.max(72, Math.min(desired, Math.max(72, maxAllowed)));
    return clamped;
  }, [containerWidth, cols, zoom]);

  // limit zoom so cellMin won't exceed container width per column
  const clampZoomForContainer = (z) => {
    const maxAllowed = Math.max(72, Math.floor((containerWidth - 12) / cols));
    const maxZoom = Math.max(0.6, maxAllowed / BASE_CELL);
    return Math.max(0.5, Math.min(z, maxZoom * 2.5)); // some upper headroom (but cellMin enforces fit)
  };

  // pinch-to-zoom (touch)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    function getDistance(touches) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    const onTouchStart = (e) => {
      if (e.touches && e.touches.length === 2) {
        touchState.current.initialDistance = getDistance(e.touches);
        touchState.current.initialZoom = zoom;
      }
    };

    const onTouchMove = (e) => {
      if (e.touches && e.touches.length === 2 && touchState.current.initialDistance) {
        // prevent page pinch-zoom
        if (e.cancelable) e.preventDefault();
        const newDist = getDistance(e.touches);
        const scaleChange = newDist / touchState.current.initialDistance;
        const newZoom = clampZoomForContainer(touchState.current.initialZoom * scaleChange);
        setZoom(newZoom);
      }
    };

    const onTouchEnd = (e) => {
      // reset pinch state when fingers lifted
      if (!e.touches || e.touches.length < 2) {
        touchState.current.initialDistance = null;
        touchState.current.initialZoom = null;
      }
    };

    // use passive:false for touchmove so we can preventDefault
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [zoom, containerWidth, cols]);

  // if grid isn't ready
  if (!mapGrid || mapGrid.length === 0) {
    return <div style={{ padding: 12 }} className="panel">Loading map...</div>;
  }

  return (
    <div className="mt-4" ref={containerRef} style={{ width: '100%', overflowX: 'hidden' }}>
      <style>{`
        .agmap-controls { display:flex; gap:8px; flex-wrap:wrap; align-items:center; margin-bottom:10px; }
        .agmap-legend { display:flex; gap:10px; align-items:center; font-size:13px; color:#333;}
        .agmap-grid { width:100%; border-radius:10px; background:linear-gradient(180deg,#f8fff8,#ffffff); padding:8px; box-sizing:border-box; }
        .agmap-grid-content { display:grid; gap:10px; align-items:start; }
        .plot { border-radius:8px; background: #fff; box-shadow:0 4px 10px rgba(0,0,0,0.06); position:relative; padding:6px; display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:92px; cursor:pointer; user-select:none; }
        .plot-inner { width:100%; height:100%; display:flex; flex-direction:column; gap:6px; align-items:center; justify-content:center; border-radius:6px; padding:6px; box-sizing:border-box; position:relative; }
        .badge { position:absolute; top:6px; right:6px; background:rgba(0,0,0,0.7); color:#fff; padding:4px 8px; border-radius:999px; font-size:12px }
        .plot-thumb { width:48px; height:48px; border-radius:6px; object-fit:cover; box-shadow:0 2px 6px rgba(0,0,0,0.08); }
        .plot-name { font-weight:600; font-size:13px; text-align:center; max-width:100%; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .plot-sub { font-size:11px; color:#444; opacity:0.9; }
        /* responsive tweaks */
        @media (max-width:720px) {
          .plot { min-height:84px; }
          .plot-thumb { width:42px; height:42px; }
        }
      `}</style>

      {/* Controls */}
      <div className="agmap-controls bg-green-600 px-4 py-4 rounded-lg">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={() => setZoom(z => clampZoomForContainer(+(z - 0.1).toFixed(2)))}
            style={{ padding: '6px 8px' }}
            aria-label="Zoom out"
          >
            ➖
          </button>
          <div className="text-white" style={{ minWidth: 44, textAlign: 'center' }}>{Math.round(zoom * 100)}%</div>
          <button
            onClick={() => setZoom(z => clampZoomForContainer(+(z + 0.1).toFixed(2)))}
            style={{ padding: '6px 8px' }}
            aria-label="Zoom in"
          >
            ➕
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={showOwners} onChange={e => setShowOwners(e.target.checked)} /> Owners
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={showHeatmap} onChange={e => setShowHeatmap(e.target.checked)} /> Heatmap
          </label>
        </div>

        <div style={{ flex: 1 }} />

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="agmap-legend" aria-hidden>
            <div style={{ width: 120, height: 10, borderRadius: 6, background: 'linear-gradient(90deg,#25a032,#ffc83c,#e13232)' }} />
            <div style={{ fontSize: 12, color: '#222' }}>Price heat</div>
          </div>
          <div style={{ marginLeft: 8 }}>
            <input
              placeholder="Search listing / location"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #ddd' }}
            />
          </div>
        </div>
      </div>

      {/* Map grid - uses cellMin to ensure no horizontal overflow */}
      <div className="agmap-grid">
        <div
          className="agmap-grid-content"
          style={{
            gridTemplateColumns: `repeat(${cols}, minmax(${cellMin}px, 1fr))`,
            // ensure each cell minimum is controlled via cellMin which is derived from container width,
            // so sum(min) <= containerWidth and horizontal scroll is avoided.
          }}
        >
          {mapGrid.map((row, r) =>
            row.map((cell, c) => {
              const items = cell.items || [];
              const visible = cellMatchesSearch(cell);
              if (!visible) {
                return (
                  <div key={`${r}-${c}`} style={{ opacity: 0.12 }} className="plot">
                    <div className="plot-inner">—</div>
                  </div>
                );
              }

              const prices = items.map(it => Number(it.price || it.offerPrice || it.marketPrice || 0)).filter(p => p > 0);
              const avgPrice = prices.length ? Math.round(prices.reduce((a, b) => a + b, 0) / prices.length) : null;
              const heatColor = avgPrice && showHeatmap ? priceToColor(avgPrice) : undefined;

              const ownedByPlayer = items.some(it => it.owner && ownerId && it.owner === ownerId);
              const anyOwned = items.some(it => it.owner);
              const bg = showOwners && anyOwned ? (ownedByPlayer ? '#e9ffef' : '#fff7e6') : undefined;

              return (
                <div
                  role="button"
                  key={`${r}-${c}`}
                  className="plot"
                  onClick={() => openCell(r, c)}
                  title={`${items.length} item(s)` + (avgPrice ? ` • avg ₹${avgPrice}` : '')}
                  aria-label={`Plot ${r + 1}, ${c + 1} with ${items.length} items`}
                  style={{
                    border: showOwners && anyOwned ? '1px solid rgba(0,0,0,0.06)' : '1px solid transparent',
                  }}
                >
                  {items.length > 0 && <div className="badge">{items.length}</div>}
                  <div
                    className="plot-inner"
                    style={{
                      background: bg || (heatColor ? heatColor : '#fff'),
                      transition: 'background .18s ease',
                    }}
                  >
                    {items.length > 0 ? (
                      <>
                        <img
                          className="plot-thumb"
                          src={items[0].img || items[0].image || `https://via.placeholder.com/80?text=${encodeURIComponent(items[0].name || 'Item')}`}
                          alt={items[0].name || 'item'}
                        />
                        <div className="plot-name">{items[0].name || items[0].id}</div>
                        <div className="plot-sub">{items[0].location || items[0].area || ''}</div>
                        {avgPrice && <div style={{ fontSize: 12, marginTop: 4 }}>avg ₹{avgPrice}</div>}
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: 24 }}>🟩</div>
                        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Unowned</div>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Side details drawer - responsive: right drawer on wide screens, bottom sheet on narrow screens */}
      {selectedCell && (() => {
        const isNarrow = typeof window !== 'undefined' && window.innerWidth < 760;
        const drawerStyle = isNarrow
          ? { position: 'fixed', left: 12, right: 12, bottom: 12, height: '40%', zIndex: 60, background: '#fff', boxShadow: '0 10px 30px rgba(0,0,0,0.12)', borderRadius: 12, overflow: 'auto', padding: 12 }
          : { position: 'fixed', right: 12, top: 80, bottom: 20, width: 360, zIndex: 60, background: '#fff', boxShadow: '0 10px 30px rgba(0,0,0,0.12)', borderRadius: 12, overflow: 'auto', padding: 12 };

        return (
          <div style={drawerStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>Cell {selectedCell.r + 1}, {selectedCell.c + 1}</strong>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { navigator.clipboard?.writeText(`Cell ${selectedCell.r+1},${selectedCell.c+1}`); alert('Copied cell coords (dummy)'); }}>Copy</button>
                <button onClick={closeDetails}>✖</button>
              </div>
            </div>

            <div style={{ marginTop: 10 }}>
              {selectedCell.items.length === 0 ? (
                <div style={{ color: '#666' }}>No items in this cell</div>
              ) : selectedCell.items.map(it => (
                <div key={it.id || it.name} style={{ display: 'flex', gap: 10, padding: 8, borderBottom: '1px solid #eee', alignItems: 'center' }}>
                  <img src={it.img || `https://via.placeholder.com/80?text=${encodeURIComponent(it.name||'')}`} alt={it.name} style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>{it.name || it.id}</div>
                    <div style={{ fontSize: 12, color: '#666' }}>{it.location || it.area || ''}</div>
                    <div style={{ marginTop: 6 }}>
                      <span style={{ textDecoration: it.marketPrice && it.marketPrice > (it.offerPrice || 0) ? 'line-through' : 'none', color: '#777', marginRight: 8 }}>
                        ₹{it.marketPrice || it.price || it.market || '-'}
                      </span>
                      <span style={{ fontWeight: 800, color: '#138000' }}>₹{it.offerPrice || it.price || '-'}</span>
                      {it.unit ? <span style={{ marginLeft: 6, fontSize: 12, color: '#444' }}>/ {it.unit}</span> : null}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <button onClick={() => contactOwner(it)} style={{ padding: '6px 8px', background: '#0b7f3b', color: '#fff', borderRadius: 8 }}>Contact</button>
                    <button onClick={() => scheduleVisit(it)} style={{ padding: '6px 8px', background: '#fff', border: '1px solid #ddd', borderRadius: 8 }}>Visit</button>
                    <button onClick={() => makeOffer(it)} style={{ padding: '6px 8px', background: '#ffd54f', borderRadius: 8 }}>Make Offer</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
